import { useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Pencil, Plus, Trash2 } from 'lucide-react';
import { calculateVPELines } from '@growthdept/blocks/gift-pricing';
import {
  SECTION_LABEL,
  effectiveSection,
  reorderPackageItems,
  sectionOf,
  updatePackageItem,
} from '@/lib/api';
import type { PackageSection } from '@/lib/api';
import {
  SECTION_ORDER,
  emptyContainers,
  moveItem,
  toUpdates,
  type Containers,
} from '@/lib/reorder';
import type { GiftPackage, GiftPackageItem } from '@/lib/types';
import { CustomItemModal } from './CustomItemModal';

const HANDMATIG_SUPPLIER_ID = 'f3842105-a90f-4b65-988f-13738f55c56f';

interface Props {
  pkg: GiftPackage;
  onRemove: (id: string) => void;
  onAddSection: (section: PackageSection) => void;
  onChanged: () => void;
}

export function PackageSections({ pkg, onRemove, onAddSection, onChanged }: Props) {
  const [customForSection, setCustomForSection] = useState<PackageSection | null>(null);
  const [editItem, setEditItem] = useState<GiftPackageItem | null>(null);
  const items = useMemo(() => pkg.items ?? [], [pkg.items]);

  // vpe-lines + item-lookup op id (volgorde-onafhankelijk, want elke line is per item).
  const { itemById, lineById } = useMemo(() => {
    const vpeLines = calculateVPELines(items as never, pkg.quantity);
    const itemMap = new Map<string, GiftPackageItem>();
    const lineMap = new Map<string, ReturnType<typeof calculateVPELines>[number]>();
    items.forEach((item, idx) => {
      itemMap.set(item.id, item);
      if (vpeLines[idx]) lineMap.set(item.id, vpeLines[idx]);
    });
    return { itemById: itemMap, lineById: lineMap };
  }, [items, pkg.quantity]);

  // Natuurlijke sectie per item (product-afgeleid) voor section_override-bepaling.
  const naturalSection = useMemo(() => {
    const map: Record<string, PackageSection> = {};
    for (const item of items) {
      map[item.id] = item.gift_product ? sectionOf(item.gift_product) : 'overig';
    }
    return map;
  }, [items]);

  // Container-state (item-ids per sectie) afgeleid uit de huidige items (al op sort_order).
  const [containers, setContainers] = useState<Containers>(emptyContainers);
  const containersRef = useRef<Containers>(containers);
  const dragOriginRef = useRef<Containers | null>(null);
  useEffect(() => {
    const next = emptyContainers();
    for (const item of items) {
      next[effectiveSection(item)].push(item.id);
    }
    containersRef.current = next;
    setContainers(next);
  }, [items]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  function handleDragOver(event: DragOverEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const current = containersRef.current;
    const next = moveItem(current, String(active.id), String(over.id));
    if (next === current) return;
    containersRef.current = next;
    setContainers(next);
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    const origin = dragOriginRef.current;
    dragOriginRef.current = null;
    if (!over) {
      if (origin) {
        containersRef.current = origin;
        setContainers(origin);
      }
      return;
    }
    const current = containersRef.current;
    const next =
      active.id === over.id ? current : moveItem(current, String(active.id), String(over.id));
    containersRef.current = next;
    setContainers(next);
    if (origin && JSON.stringify(origin) === JSON.stringify(next)) return;
    reorderPackageItems(toUpdates(next, naturalSection))
      .then(() => onChanged())
      .catch(() => onChanged()); // bij fout: refetch herstelt de waarheid
  }

  return (
    <div className="space-y-3">
      <p className="text-[10px] uppercase tracking-wider text-bmade-500">
        Inhoud van 1 pakket — sleep met de greep om te herordenen of naar een andere categorie te
        verplaatsen
      </p>
      <CustomItemModal
        open={customForSection !== null}
        onClose={() => setCustomForSection(null)}
        packageId={pkg.id}
        section={customForSection ?? 'overig'}
        onAdded={onChanged}
      />
      <CustomItemModal
        open={editItem !== null}
        onClose={() => setEditItem(null)}
        packageId={pkg.id}
        section={(editItem?.section_override as PackageSection) ?? 'overig'}
        onAdded={onChanged}
        editItem={editItem}
      />
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={() => {
          dragOriginRef.current = containersRef.current;
        }}
        onDragOver={handleDragOver}
        onDragCancel={() => {
          if (!dragOriginRef.current) return;
          containersRef.current = dragOriginRef.current;
          setContainers(dragOriginRef.current);
          dragOriginRef.current = null;
        }}
        onDragEnd={handleDragEnd}
      >
        {SECTION_ORDER.map((section) => {
          const ids = containers[section];
          const sectionRecipe = ids.reduce((sum, id) => {
            const p = itemById.get(id)?.gift_product;
            const qty = itemById.get(id)?.quantity_per_package ?? 0;
            return p ? sum + qty * p.unit_purchase_price : sum;
          }, 0);
          return (
            <SectionDroppable key={section} section={section}>
              <header className="flex items-center justify-between border-b border-bmade-200 px-3 py-2 dark:border-bmade-800">
                <div className="flex items-baseline gap-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-bmade-700 dark:text-bmade-300">
                    {SECTION_LABEL[section]}
                  </h3>
                  {sectionRecipe > 0 && (
                    <span className="text-[10px] text-bmade-500">
                      EUR {sectionRecipe.toFixed(2)} / pakket
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setCustomForSection(section)}
                    title="Handmatig item toevoegen"
                    className="inline-flex items-center gap-1 rounded-md border border-bmade-200 px-2 py-0.5 text-[11px] text-bmade-600 transition-colors hover:border-bmade-400 hover:bg-bmade-100 dark:border-bmade-700 dark:text-bmade-400 dark:hover:border-bmade-500 dark:hover:bg-bmade-800"
                  >
                    <Pencil className="h-3 w-3" />
                    Handmatig
                  </button>
                  <button
                    type="button"
                    onClick={() => onAddSection(section)}
                    className="inline-flex items-center gap-1 rounded-md border border-bmade-200 px-2 py-0.5 text-[11px] text-bmade-700 transition-colors hover:border-accent-400 hover:bg-accent-50 hover:text-accent-700 dark:border-bmade-700 dark:text-bmade-300 dark:hover:border-accent-400 dark:hover:bg-accent-500/10 dark:hover:text-accent-300"
                  >
                    <Plus className="h-3 w-3" />
                    Catalogus
                  </button>
                </div>
              </header>
              <div className="p-2">
                {ids.length === 0 ? (
                  <p className="px-1 py-2 text-[11px] text-bmade-400">
                    Nog geen {SECTION_LABEL[section].toLowerCase()} gekozen. Sleep hier een item
                    naartoe of voeg toe.
                  </p>
                ) : (
                  <SortableContext items={ids} strategy={verticalListSortingStrategy}>
                    <ul className="space-y-1.5">
                      {ids.map((id) => {
                        const item = itemById.get(id);
                        if (!item) return null;
                        return (
                          <ItemRow
                            key={id}
                            item={item}
                            line={lineById.get(id)}
                            onRemove={() => onRemove(id)}
                            onChanged={onChanged}
                            onEdit={() => setEditItem(item)}
                          />
                        );
                      })}
                    </ul>
                  </SortableContext>
                )}
              </div>
            </SectionDroppable>
          );
        })}
      </DndContext>
    </div>
  );
}

function SectionDroppable({
  section,
  children,
}: {
  section: PackageSection;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: section });
  return (
    <section
      ref={setNodeRef}
      data-testid={`package-section-${section}`}
      className={`rounded-md border bg-bmade-50/40 transition-colors dark:bg-bmade-950/30 ${
        isOver
          ? 'border-accent-400 ring-1 ring-accent-300 dark:border-accent-500/60'
          : 'border-bmade-200 dark:border-bmade-800'
      }`}
    >
      {children}
    </section>
  );
}

function ItemRow({
  item,
  line,
  onRemove,
  onChanged,
  onEdit,
}: {
  item: GiftPackageItem;
  line: ReturnType<typeof calculateVPELines>[number] | undefined;
  onRemove: () => void;
  onChanged: () => void;
  onEdit: () => void;
}) {
  const product = item.gift_product;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });
  const isHandmatig = product?.supplier_id === HANDMATIG_SUPPLIER_ID;
  const [qty, setQty] = useState(item.quantity_per_package);
  const [saving, setSaving] = useState(false);

  // Prijs-override per pakket-item. Default = product-prijs (na fetch-merge
  // bevat product.unit_purchase_price al de override). Origineel achterhalen
  // we via item.unit_purchase_price_override flag.
  const effectivePrice = product?.unit_purchase_price ?? 0;
  const isOverride = item.unit_purchase_price_override != null;
  const defaultPrice = effectivePrice;
  const [priceInput, setPriceInput] = useState(effectivePrice.toFixed(2));

  useEffect(() => {
    setQty(item.quantity_per_package);
  }, [item.quantity_per_package]);

  useEffect(() => {
    setPriceInput(effectivePrice.toFixed(2));
  }, [effectivePrice]);

  useEffect(() => {
    if (qty === item.quantity_per_package) return;
    if (qty < 0) return;
    setSaving(true);
    const t = setTimeout(() => {
      updatePackageItem(item.id, { quantity_per_package: qty })
        .then(() => onChanged())
        .finally(() => setSaving(false));
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qty]);

  async function savePrice() {
    const parsed = parseFloat(priceInput);
    if (Number.isNaN(parsed) || parsed < 0) {
      setPriceInput(effectivePrice.toFixed(2));
      return;
    }
    if (Math.abs(parsed - effectivePrice) < 0.005) return;
    setSaving(true);
    try {
      await updatePackageItem(item.id, { unit_purchase_price_override: parsed });
      onChanged();
    } finally {
      setSaving(false);
    }
  }

  async function resetPrice() {
    setSaving(true);
    try {
      await updatePackageItem(item.id, { unit_purchase_price_override: null });
      onChanged();
    } finally {
      setSaving(false);
    }
  }

  const excluded = item.excluded === true;
  const [excludedReason, setExcludedReason] = useState(item.excluded_reason ?? '');

  async function toggleExcluded() {
    const next = !excluded;
    await updatePackageItem(item.id, {
      excluded: next,
      excluded_reason: next ? excludedReason || null : null,
    });
    onChanged();
  }

  async function saveReason() {
    await updatePackageItem(item.id, { excluded_reason: excludedReason || null });
    onChanged();
  }

  if (!product) return null;

  const recipeLine = qty * effectivePrice;
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  };

  return (
    <li
      ref={setNodeRef}
      data-testid={`package-item-${item.id}`}
      style={style}
      className={`rounded border border-transparent bg-white p-2 text-xs transition-colors hover:border-bmade-200 dark:bg-bmade-900/40 dark:hover:border-bmade-700 ${
        excluded ? 'opacity-60 ring-1 ring-amber-300 dark:ring-amber-500/40' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-start gap-1.5">
          <button
            type="button"
            {...attributes}
            {...listeners}
            title="Sleep om te herordenen of te verplaatsen"
            className="mt-0.5 cursor-grab touch-none rounded p-0.5 text-bmade-400 hover:bg-bmade-100 hover:text-bmade-600 active:cursor-grabbing dark:hover:bg-bmade-800"
          >
            <GripVertical className="h-3.5 w-3.5" />
          </button>
          <div className="min-w-0 flex-1">
            <div
              className={`group flex items-center gap-1 truncate font-medium text-bmade-950 dark:text-white ${excluded ? 'line-through' : ''}`}
            >
              <span className="truncate">{product.name}</span>
              {isHandmatig && (
                <button
                  type="button"
                  onClick={onEdit}
                  className="shrink-0 rounded p-0.5 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-bmade-100 dark:hover:bg-bmade-800"
                  title="Item bewerken"
                >
                  <Pencil className="h-3 w-3 text-bmade-400" />
                </button>
              )}
            </div>
            <div className="text-[11px] text-bmade-600 dark:text-bmade-400">
              {product.supplier?.name ?? '—'}
              {product.brand && ` · ${product.brand}`}
            </div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={toggleExcluded}
            title={excluded ? 'Vermijd-vinkje opheffen' : 'Markeer als vermijd voor klant'}
            className={`rounded p-1 transition-colors ${
              excluded
                ? 'text-amber-600 hover:bg-amber-100 dark:text-amber-400 dark:hover:bg-amber-500/15'
                : 'text-bmade-400 hover:bg-bmade-100 hover:text-amber-600 dark:hover:bg-bmade-800 dark:hover:text-amber-400'
            }`}
          >
            ⚠
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="rounded p-1 text-bmade-500 transition-colors hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-500/10 dark:hover:text-red-400"
            title="Verwijderen"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </div>

      {excluded && (
        <div className="mt-2 flex items-center gap-2 rounded bg-amber-50 px-2 py-1 dark:bg-amber-500/10">
          <input
            type="text"
            value={excludedReason}
            onChange={(e) => setExcludedReason(e.target.value)}
            onBlur={saveReason}
            placeholder="reden (bv. allergie noten)"
            className="flex-1 bg-transparent text-[11px] text-amber-800 outline-none placeholder:text-amber-500 dark:text-amber-200"
          />
        </div>
      )}

      <div className="mt-2 flex items-center justify-between gap-2 text-[11px]">
        <div className="flex items-center gap-1.5">
          <input
            type="number"
            min={0}
            step={1}
            value={qty}
            onChange={(e) => setQty(parseInt(e.target.value) || 0)}
            className="w-12 rounded border border-bmade-200 bg-white px-1 py-0.5 text-center text-sm font-semibold text-bmade-950 outline-none focus:border-accent-500 dark:border-bmade-700 dark:bg-bmade-950 dark:text-white"
          />
          <span className="text-bmade-500">× EUR</span>
          <input
            type="number"
            min={0}
            step={0.01}
            value={priceInput}
            onChange={(e) => setPriceInput(e.target.value)}
            onBlur={savePrice}
            title={
              isOverride
                ? `Override (origineel EUR ${defaultPrice.toFixed(2)})`
                : 'Pas inkoopprijs voor dit pakket aan'
            }
            className={`w-16 rounded border px-1 py-0.5 text-center text-sm font-semibold outline-none focus:border-accent-500 dark:bg-bmade-950 ${
              isOverride
                ? 'border-accent-400 bg-accent-50 text-accent-700 dark:border-accent-500/50 dark:bg-accent-500/10 dark:text-accent-300'
                : 'border-bmade-200 bg-white text-bmade-950 dark:border-bmade-700 dark:text-white'
            }`}
          />
          <span className="text-bmade-700 dark:text-bmade-300">
            = EUR {recipeLine.toFixed(2)} / pakket
          </span>
          {isOverride && (
            <button
              type="button"
              onClick={resetPrice}
              title={`Reset naar standaard EUR ${defaultPrice.toFixed(2)}`}
              className="text-[10px] text-bmade-400 hover:text-bmade-700 dark:hover:text-white"
            >
              reset
            </button>
          )}
          {saving && <span className="text-[9px] text-bmade-400">saving...</span>}
        </div>
      </div>

      {line && qty > 0 && (
        <div className="mt-1 grid grid-cols-4 gap-2 border-t border-bmade-100 pt-1 text-[10px] text-bmade-500 dark:border-bmade-800 dark:text-bmade-400">
          <div>colli {product.vpe}</div>
          <div className="font-medium text-bmade-700 dark:text-bmade-300">
            {line.vpe_units_needed} colli bestellen
          </div>
          <div>= {line.total_ordered} artikelen</div>
          <div className={line.leftover_units > 0 ? 'text-amber-600 dark:text-amber-400' : ''}>
            rest {line.leftover_units} (EUR {line.leftover_value.toFixed(2)})
          </div>
        </div>
      )}
    </li>
  );
}
